import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import CSVImportModal from '../components/CSVImportModal';
import api, { API_URL } from '../utils/api';
import staffService from '../services/staffService';
import campusService from '../services/campusService';
import { Plus, MagnifyingGlass, X, Upload, Download, FileCsv, CircleNotch, LockKey, CheckCircle } from '@phosphor-icons/react';

function formatCurrency(amount) {
  if (!amount) return '—';
  return `GHS ${parseFloat(amount).toLocaleString('en-GH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [credModal, setCredModal] = useState(null);
  const [credLoading, setCredLoading] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [campusFilter, setCampusFilter] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: 'male',
    staff_type: 'teaching',
    role: '',
    position: '',
    department: '',
    date_joined: new Date().toISOString().split('T')[0],
    campus_id: ''
  });

  const loadStaff = useCallback(() => {
    setLoading(true);
    staffService.listStaff({ search, campus_id: campusFilter || undefined })
      .then(data => setStaff(data.items || []))
      .catch(err => toast.error('Failed to load staff'))
      .finally(() => setLoading(false));
  }, [search, campusFilter]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    campusService.listCampuses()
      .then(data => setCampuses(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to load campuses:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await staffService.createStaff(formData);
      toast.success('Staff added successfully!');
      setShowModal(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        gender: 'male',
        staff_type: 'teaching',
        role: '',
        position: '',
        department: '',
        date_joined: new Date().toISOString().split('T')[0],
        campus_id: ''
      });
      loadStaff();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleImport = async (file) => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a valid CSV file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const result = await staffService.importStaff(formData);

      if (result.success) {
        toast.success(result.message);
        if (result.errors && result.errors.length > 0) {
          // Show partial success with errors
          toast.warning(`Imported ${result.success_count} staff members with ${result.errors.length} errors. Check console for details.`);
          console.log('Import errors:', result.errors);
        }
      } else {
        toast.error(result.message);
        if (result.errors && result.errors.length > 0) {
          console.log('Import errors:', result.errors);
        }
      }

      setShowImportModal(false);
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${API_URL}/templates/staff_import_template.csv`;
    link.download = 'staff_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreatePortalCredentials = async (member) => {
    setCredLoading(true);
    try {
      const res = await staffService.regeneratePortalCredentials(member.id);
      const creds = res.portal_account || res;
      setCredModal({ name: member.full_name, email: creds.email, password: creds.password });
    } catch (err) {
      toast.error(err?.detail || err?.response?.data?.detail || 'Failed to create portal credentials');
    } finally {
      setCredLoading(false);
    }
  };

  const requiredColumns = [
    { name: 'staff_id', description: 'Unique staff identifier (e.g., STF001, STF002) - No duplicates allowed' },
    { name: 'first_name', description: "Staff's first name" },
    { name: 'last_name', description: "Staff's last name" },
    { name: 'email', description: 'Email address (must be unique per school)' },
    { name: 'phone', description: 'Phone number (e.g., 0201234567)' },
    { name: 'date_of_birth', description: 'Birth date (Format: YYYY-MM-DD, e.g., 1985-06-15)' },
    { name: 'gender', description: "'male' or 'female' (case-insensitive)", values: ['male', 'female'] },
    { name: 'staff_type', description: "'teaching', 'non_teaching', 'admin'" },
    { name: 'position', description: 'Job title (e.g., Mathematics Teacher, Principal)' },
    { name: 'date_joined', description: 'Enrollment date (Format: YYYY-MM-DD)' }
  ];

  const optionalColumns = [
    { name: 'other_names', description: 'Middle or additional names' },
    { name: 'department', description: 'Department/subject area (e.g., Science, Languages)' },
    { name: 'qualification', description: 'Academic qualification (e.g., BSc Mathematics)' },
    { name: 'address', description: 'Residential address' },
    { name: 'photo_url', description: 'URL to staff photo' }
  ];

  const staffTypeValues = {
    'Teaching Staff': ['teaching', 'teacher'],
    'Non-Teaching Staff': ['non_teaching', 'support'],
    'Administrative': ['admin', 'administrator']
  };

  return (
    <Layout>
      <div className="page" data-testid="staff-page">
        <div className="page-header">
          <div>
            <h1>Staff Management</h1>
            <p>Manage teachers and administrative staff</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => setShowImportModal(true)} data-testid="import-staff-btn">
              <Upload size={18} weight="bold" /> Import CSV
            </button>
            <button className="btn btn-primary" onClick={() => setShowModal(true)} data-testid="add-staff-btn">
              <Plus size={18} weight="bold" /> Add Staff
            </button>
          </div>
        </div>

        <div className="search-bar">
          <div className="search-input-wrapper">
            <MagnifyingGlass size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search staff..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="staff-search-input"
            />
          </div>
          {campuses.length > 0 && (
            <select
              value={campusFilter}
              onChange={(e) => setCampusFilter(e.target.value)}
              data-testid="staff-campus-filter"
              style={{ marginLeft: 8 }}
            >
              <option value="">All Campuses</option>
              {campuses.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading staff...</div>
        ) : (
          <div className="table-container">
            <table className="data-table" data-testid="staff-table">
              <thead>
                <tr>
                  <th>Staff ID</th>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Department</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Payroll Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map(member => (
                  <tr key={member.id} data-testid={`staff-row-${member.id}`}>
                    <td className="mono">{member.staff_id}</td>
                    <td>{member.full_name}</td>
                    <td>{member.position}</td>
                    <td>{member.department || '-'}</td>
                    <td className="capitalize">{member.staff_type?.replace('_', ' ')}</td>
                    <td>
                      <span className={`badge ${member.status}`}>{member.status}</span>
                    </td>
                    <td>
                      {member.payroll_contract ? (
                        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                          <span style={{ color: '#064E3B', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> {formatCurrency(member.payroll_contract.basic_salary)}</span>
                          <br />
                          <small style={{ color: '#666' }}>Tax: {member.payroll_contract.tax_rate_percent}%</small>
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: '12px' }}>No contract</span>
                      )}
                    </td>
                    <td>
                      {member.payroll_contract && (
                        <Link to={`/payroll?staff=${member.id}`} className="btn btn-sm" style={{ marginRight: '4px' }}>Payroll</Link>
                      )}
                      <button
                        className="btn btn-sm"
                        style={{ marginRight: '4px' }}
                        onClick={() => handleCreatePortalCredentials(member)}
                        disabled={credLoading}
                        title="Create portal account"
                      >
                        <LockKey size={14} weight="bold" />
                      </button>
                      <Link to={`/staff/${member.id}`} className="btn btn-sm">View</Link>
                    </td>
                  </tr>
                ))}
                {staff.length === 0 && (
                  <tr>
                    <td colSpan="8" className="empty-state">No staff found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="add-staff-modal">
              <div className="modal-header">
                <h2>Add New Staff</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Staff Type</label>
                    <select
                      value={formData.staff_type}
                      onChange={(e) => setFormData({...formData, staff_type: e.target.value})}
                      disabled={submitting}
                      data-testid="staff-type-select"
                    >
                      <option value="teaching">Teaching</option>
                      <option value="non_teaching">Non-Teaching</option>
                      <option value="admin">Administrative</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      disabled={submitting}
                      required
                      data-testid="staff-role-select"
                    >
                      <option value="" disabled>Select a role</option>
                      <option value="school_admin">School Admin</option>
                      <option value="hr">HR</option>
                      <option value="teacher">Teacher</option>
                      <option value="security_officer">Security Officer</option>
                      <option value="driver">Driver</option>
                      <option value="nurse">Nurse</option>
                      <option value="registrar">Registrar</option>
                      <option value="storekeeper">Storekeeper</option>
                      <option value="canteen_staff">Canteen Staff</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      disabled={submitting}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      disabled={submitting}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      disabled={submitting}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      disabled={submitting}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      disabled={submitting}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => setFormData({...formData, gender: e.target.value})}
                      disabled={submitting}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Position</label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({...formData, position: e.target.value})}
                      placeholder="e.g., Mathematics Teacher"
                      disabled={submitting}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Department</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({...formData, department: e.target.value})}
                      placeholder="e.g., Science"
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Date Joined</label>
                  <input
                    type="date"
                    value={formData.date_joined}
                    onChange={(e) => setFormData({...formData, date_joined: e.target.value})}
                    disabled={submitting}
                    required
                  />
                </div>
                {campuses.length > 0 && (
                  <div className="form-group">
                    <label>Campus</label>
                    <select
                      value={formData.campus_id}
                      onChange={(e) => setFormData({...formData, campus_id: e.target.value})}
                      disabled={submitting}
                      data-testid="staff-campus-select"
                    >
                      <option value="">No campus</option>
                      {campuses.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" data-testid="save-staff-btn">
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : (
                      'Save Staff'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showImportModal && (
          <CSVImportModal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            title="Import Staff from CSV"
            entityName="staff"
            requiredColumns={requiredColumns}
            optionalColumns={optionalColumns}
            staffTypeValues={staffTypeValues}
            onImport={handleImport}
            onDownloadTemplate={downloadTemplate}
            importing={importing}
          />
        )}
      </div>

      {credModal && (
        <div className="modal-overlay" onClick={() => setCredModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Portal Account Created</h2>
              <button className="modal-close" onClick={() => setCredModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-content">
              <p style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                Share these credentials securely. The user must change their password on first login.
              </p>
              <div style={{ background: '#f0f4f0', borderRadius: 8, padding: '14px 18px', fontFamily: 'monospace', fontSize: 14, marginBottom: 16 }}>
                <div><strong>Name:</strong> {credModal.name}</div>
                <div style={{ marginTop: 8 }}><strong>Email:</strong> {credModal.email}</div>
                <div style={{ marginTop: 8 }}><strong>Password:</strong> <span style={{ background: '#d4e8d4', padding: '2px 8px', borderRadius: 4 }}>{credModal.password}</span></div>
              </div>
              <p style={{ fontSize: 12, color: '#888' }}>This password will not be shown again.</p>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={() => setCredModal(null)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default StaffPage;
